import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Collapse, UncontrolledTooltip } from "reactstrap";
import withRouter from "../../Components/Common/withRouter";
import { withTranslation } from "react-i18next";
import { useNavData } from "Components/Hooks/useNavData";

const HorizontalLayout: React.FC<any> = (props) => {
	const [isMoreMenu, setIsMoreMenu] = useState(false);
	const [menuState, setMenuState] = useState<Record<string, boolean>>({
		dashboard: false,
		students: false,
		applications: false,
		institutes: false,
		courses: false,
		finances: false,
		sources: false,
		subagents: false,
		channels: false,
		fields: false,
		users: false,
		roles: false,
		teams: false,
		companies: false,
		forms: false,
		products: false,
		templates: false,
		reports: false,
		imports: false,
	});
	const [currentState, setCurrentState] = useState("Dashboard");

	const updateIconSidebar = useCallback((e: any) => {
		if (e && e.target && e.target.getAttribute("sub-items")) {
			const ul = document.getElementById("two-column-menu");
			if (ul) {
				const iconItems = ul.querySelectorAll(".nav-icon.active");
				Array.from(iconItems).forEach((item) => {
					const element = item as HTMLElement;
					element.classList.remove("active");
					const id = element.getAttribute("sub-items");
					const targetEl = document.getElementById(id || "");
					if (targetEl) {
						targetEl.classList.remove("show");
					}
				});
			}
		}
	}, []);

	const navData = useNavData(
		menuState,
		setMenuState,
		setCurrentState,
		updateIconSidebar
	);
	const path = props.router.location.pathname;

	// Limit visible items; overflow items go under "More"
	const MAX_VISIBLE_ITEMS = 6;
	const menuItems = navData.slice(0, MAX_VISIBLE_ITEMS);
	const overflowItems =
		navData.length > MAX_VISIBLE_ITEMS ? navData.slice(MAX_VISIBLE_ITEMS) : [];

	const finalMenuItems = [...menuItems];
	if (overflowItems.length > 0) {
		finalMenuItems.push({
			id: "more",
			label: "More",
			icon: "ri-more-2-fill",
			link: "/#",
			stateVariables: isMoreMenu,
			subItems: overflowItems,
			click: (e: any) => {
				e.preventDefault();
				setIsMoreMenu(!isMoreMenu);
			},
		});
	}

	const initMenu = useCallback(() => {
		const pathName = (process.env.PUBLIC_URL || "") + path;
		const ul = document.getElementById("navbar-nav");
		if (!ul) return;
		const items = ul.getElementsByTagName("a");
		const itemsArray = Array.from(items) as HTMLAnchorElement[];
		removeActivation(itemsArray);
		const matchingMenuItem = itemsArray.find((x) => x.pathname === pathName);
		if (matchingMenuItem) activateParentDropdown(matchingMenuItem);
	}, [path]);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
		initMenu();
	}, [path, initMenu]);

	useEffect(() => {
		document.body.classList.remove("twocolumn-panel");
		setMenuState((prev) => {
			const updated = { ...prev };
			Object.keys(updated).forEach((key) => {
				if (key.toLowerCase() !== currentState.toLowerCase()) {
					updated[key] = false;
				}
			});
			return updated;
		});
	}, [currentState]);

	function activateParentDropdown(item: HTMLElement) {
		item.classList.add("active");
		const parentCollapseDiv = item.closest(".collapse.menu-dropdown");
		if (parentCollapseDiv) {
			parentCollapseDiv.classList.add("show");
			const trigger = parentCollapseDiv.parentElement?.children[0];
			if (trigger) {
				trigger.classList.add("active");
				trigger.setAttribute("aria-expanded", "true");
			}
		}
	}

	const removeActivation = (items: HTMLAnchorElement[]) => {
		items.forEach((item) => {
			item.classList.remove("active");
			item.setAttribute("aria-expanded", "false");
			if (item.nextElementSibling) item.nextElementSibling.classList.remove("show");
		});
	};

	const renderMenuItem = (item: any, key: number) => {
		const isActive = path === item.link || path.includes(item.id);
		// Use the locked property directly.
		const isLocked = item.locked;
		const hasSubItems =
			Array.isArray(item.subItems) && item.subItems.length > 0;

		const leftPadding = "ps-3";
		const rightPadding = hasSubItems ? "pe-0" : "pe-3";
		const baseClasses = `nav-link menu-link d-flex align-items-center rounded py-2 ${leftPadding} ${rightPadding}`;
		const stateClasses = isLocked
			? "text-muted cursor-not-allowed"
			: isActive
				? "text-primary fw-semibold bg-light"
				: "text-body hover-bg-light";
		const linkClasses = `${baseClasses} ${stateClasses}`;
		const tooltipId = `menu-item-${item.id}`;

		const content = hasSubItems ? (
			<span
				id={isLocked ? tooltipId : undefined}
				role="button"
				onClick={(e) => {
					if (!isLocked && item.click) item.click(e);
				}}
				className={linkClasses}
			>
				<i className={item.icon}></i>
				<span className={isLocked ? "text-truncate flex-grow-1 pe-2" : "text-truncate flex-grow-1"}>
        {props.t(item.label)}
				</span>
				{isLocked && <i className="ri-lock-line small text-muted" />}
				<i className="ri-arrow-down-s-line small text-muted" />
			</span>
		) : (
			<Link
				id={isLocked ? tooltipId : undefined}
				to={isLocked ? "/#" : item.link || "/#"}
				className={linkClasses}
				onClick={isLocked ? (e) => e.preventDefault() : undefined}
			>
				<i className={item.icon}></i>
				<span className={isLocked ? "text-truncate flex-grow-1 pe-2" : "text-truncate flex-grow-1"}>
        {props.t(item.label)}
				</span>
				{isLocked && <i className="ri-lock-line small text-muted" />}
			</Link>
		);

		return (
			<li className="nav-item" key={key}>
				{content}
				{isLocked && (
					<UncontrolledTooltip target={tooltipId} placement="top">
						{item.tooltipText || "Feature is disabled"}
					</UncontrolledTooltip>
				)}
				{hasSubItems && (
					<Collapse
						isOpen={item.stateVariables}
						className="menu-dropdown"
						style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
					>
						<ul className="nav flex-column gap-1">
							{item.subItems.map((subItem: any, subKey: number) => {
								const isSubActive = path === subItem.link;
								const isSubLocked = subItem.locked;
								const subTooltipId = `menu-item-${subItem.id}`;
								return (
									<li className="nav-item" key={subKey}>
										<Link
											id={isSubLocked ? subTooltipId : undefined}
											to={isSubLocked ? "/#" : subItem.link || "/#"}
											className={`nav-link d-flex align-items-center py-1 rounded ${isSubLocked
												? "text-muted cursor-not-allowed"
												: isSubActive
													? "text-primary fw-semibold bg-light"
													: "text-body hover-bg-light"
												}`}
											onClick={isSubLocked ? (e) => e.preventDefault() : undefined}
										>
											<span className="text-truncate flex-grow-1">
                      {props.t(subItem.label)}
											</span>
											{isSubLocked && (
												<i className="ri-lock-line small text-muted" />
											)}
										</Link>
										{isSubLocked && (
											<UncontrolledTooltip target={subTooltipId} placement="top">
												{subItem.tooltipText || "Feature is disabled"}
											</UncontrolledTooltip>
										)}
									</li>
								);
							})}
						</ul>
					</Collapse>
				)}
			</li>
		);
	};

	return <>{finalMenuItems.map(renderMenuItem)}</>;
};

HorizontalLayout.propTypes = {
	location: PropTypes.object,
	t: PropTypes.any,
};

export default withRouter(withTranslation()(HorizontalLayout));
